import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheckCircle, FiAlertCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';

const NotificationIcons = {
  success: FiCheckCircle,
  error: FiAlertCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
};

const colorSchemes = {
  success: {
    bg: '#c6f6d5', // Light green
    border: '#38a169', // Green
    text: '#276749', // Dark green
    hover: '#9ae6b4', // Medium green
  },
  error: {
    bg: '#fed7d7', // Light red
    border: '#e53e3e', // Red
    text: '#c53030', // Dark red
    hover: '#feb2b2', // Medium red
  },
  warning: {
    bg: '#feebc8', // Light orange
    border: '#ed8936', // Orange
    text: '#c05621', // Dark orange
    hover: '#fbd38d', // Medium orange
  },
  info: {
    bg: '#bee3f8', // Light blue
    border: '#3182ce', // Blue
    text: '#2b6cb0', // Dark blue
    hover: '#90cdf4', // Medium blue
  },
};

const MotionDiv = motion.div;

const Notification = ({ type = 'info', message, onClose, id }) => {
  const colors = colorSchemes[type] || colorSchemes.info;
  const Icon = NotificationIcons[type] || NotificationIcons.info;

  return (
    <MotionDiv
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      style={{
        marginBottom: '12px',
        width: '100%',
      }}>
      <div
        style={{
          backgroundColor: colors.bg,
          borderLeft: `4px solid ${colors.border}`,
          padding: '12px',
          borderRadius: '6px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          maxWidth: '100%',
        }}>
        <div
          style={{
            color: colors.text,
            fontSize: '20px',
            marginRight: '12px',
            marginLeft: '4px',
          }}>
          <Icon />
        </div>

        <span
          style={{
            flex: '1',
            fontSize: '14px',
            fontWeight: '500',
            color: colors.text,
          }}>
          {message}
        </span>

        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            right: '8px',
            top: '8px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: colors.text,
            fontSize: '18px',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            padding: 0,
          }}
          onMouseOver={e => {
            e.currentTarget.style.backgroundColor = colors.hover;
          }}
          onMouseOut={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}>
          ×
        </button>
      </div>
    </MotionDiv>
  );
};

const NotificationsContainer = ({ notifications, removeNotification }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 1000,
        maxWidth: '350px',
        width: '95%',
      }}>
      <AnimatePresence>
        {notifications.map(notification => (
          <Notification
            key={notification.id}
            id={notification.id}
            type={notification.type}
            message={notification.message}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

// Usage example component
const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = React.useState([]);

  const addNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now().toString();
    const newNotification = { id, message, type };

    setNotifications(prevNotifications => [...prevNotifications, newNotification]);

    if (duration) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  const removeNotification = id => {
    setNotifications(prevNotifications =>
      prevNotifications.filter(notification => notification.id !== id)
    );
  };

  return (
    <>
      {children}
      <NotificationsContainer
        notifications={notifications}
        removeNotification={removeNotification}
      />
    </>
  );
};

export { Notification, NotificationsContainer, NotificationsProvider };
